// Phase 37 · 23a — the board column, in the theme.
//
// Repaint only: the droppable, the sentinel-based load-more and the WIP-limit
// arithmetic are exactly as they were. Two rules from the design survive into
// the code because they carry meaning rather than taste:
//
//  1. The stage's own colour stays, as a 3px top rule and the header dot. A
//     configured stage colour is tenant DATA, not palette, and a lane has to
//     be findable while a card is mid-drag.
//  2. The WIP count turns burgundy when the stage is over its limit and
//     terracotta as it approaches. A count of "59 / 100000" is only worth
//     printing if exceeding it shows.
import { useRef, useEffect, memo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Loader2 } from 'lucide-react';
import { TicketKanbanCard } from './TicketKanbanCard';
import type { Stage } from '@/types/pipeline';
import type { Ticket } from '@centraoneplus/shared-types';

interface BoardColumnProps {
  stage: Stage;
  tickets: Ticket[];
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  onTicketClick: (ticketId: string) => void;
  onLoadMore: () => void;
}

export const BoardColumn = memo(function BoardColumn({
  stage,
  tickets,
  totalCount,
  hasMore,
  isLoadingMore,
  onTicketClick,
  onLoadMore,
}: BoardColumnProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: stage.id,
    data: { stage },
  });

  const isAtLimit = !!(stage.wipLimit && totalCount >= stage.wipLimit);
  const isNearLimit = !!(stage.wipLimit && totalCount >= Math.ceil(stage.wipLimit * 0.8));

  const stageColor = stage.color || 'var(--co-ink-3)';

  // Sentinel-based infinite scroll via IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || isLoadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore();
      },
      { rootMargin: '100px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, isLoadingMore, onLoadMore]);

  const countStyle: React.CSSProperties = isAtLimit
    ? {
        background: 'var(--co-burgundy-wash)',
        borderColor: 'var(--co-burgundy-line)',
        color: 'var(--co-burgundy)',
      }
    : isNearLimit
      ? {
          background: 'var(--co-terracotta-wash)',
          borderColor: 'var(--co-terracotta-line)',
          color: 'var(--co-terracotta-ink)',
        }
      : {
          background: 'var(--co-selected)',
          borderColor: 'transparent',
          color: 'var(--co-ink-2)',
        };

  return (
    // The ENTIRE column is the drop zone — header, divider, card area, empty
    // space below cards. Previously the droppable only covered the card area,
    // which made narrow/empty columns (New, Resolved when empty) hard to drop
    // onto; drops on the header silently produced `over=null`.
    <div
      ref={setNodeRef}
      style={{ borderRadius: 10, boxShadow: 'var(--co-shadow-card)' }}
      className={`
        flex min-w-[220px] max-w-[380px] flex-1 flex-col overflow-hidden border bg-[var(--co-raised)]
        transition-colors duration-150
        ${isOver
          ? isAtLimit
            ? 'border-[var(--co-burgundy)] ring-2 ring-inset ring-[var(--co-burgundy-line)]'
            : 'border-[var(--co-brass)] ring-2 ring-inset ring-[var(--co-brass-wash)]'
          : 'border-[var(--co-line)]'
        }
      `}
    >
      {/* Sticky header cluster — stage rule + name/count stay pinned to the top
          of the board's vertical scroll while the cards scroll under it. Fully
          OPAQUE bg (not translucent) so scrolling cards are completely hidden
          behind it. */}
      <div className="sticky top-0 z-10 flex-shrink-0 bg-[var(--co-raised)]">
        {/* The stage's configured colour, full-bleed. */}
        <div className="h-[3px]" style={{ background: stageColor }} />

        <div className="flex items-center gap-2 border-b border-[var(--co-line-subtle)] px-[14px] pb-[11px] pt-[13px]">
          <span
            className="h-[7px] w-[7px] flex-none rounded-full"
            style={{ backgroundColor: stageColor }}
          />
          <h3 className="co-mono min-w-0 flex-1 truncate text-[10px] font-semibold uppercase tracking-[.13em] text-[var(--co-ink)]">
            {stage.name}
          </h3>
          <span
            className="co-mono flex-none whitespace-nowrap rounded-[5px] border px-[7px] py-[3px] text-[10px] font-semibold leading-none tracking-[.06em] tabular-nums"
            style={countStyle}
            title={stage.wipLimit ? `${totalCount} of a ${stage.wipLimit} WIP limit` : `${totalCount} tickets`}
          >
            {totalCount}
            {stage.wipLimit ? (
              <span className="font-normal text-[var(--co-ink-3)]"> / {stage.wipLimit}</span>
            ) : null}
          </span>
          {isAtLimit && (
            <span className="co-mono flex-none text-[9px] font-semibold uppercase tracking-[.11em] text-[var(--co-burgundy)]">
              Full
            </span>
          )}
        </div>
      </div>

      {/* Card area — flex-1 so it fills remaining height and catches drops on
          empty space. The well is one step back from the column shell so the
          cards read as sitting IN the lane. */}
      <div className="flex min-h-[120px] flex-1 flex-col gap-[9px] bg-[var(--co-surface)] p-[11px]">
        <SortableContext items={tickets.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {tickets.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-[var(--co-line-strong)] py-10">
              <p className="co-mono text-[9.5px] font-semibold uppercase tracking-[.1em] text-[var(--co-ink-4)]">
                Drop tickets here
              </p>
            </div>
          ) : (
            tickets.map((ticket) => (
              <TicketKanbanCard
                key={ticket.id}
                ticket={ticket}
                onClick={() => onTicketClick(ticket.id)}
              />
            ))
          )}
        </SortableContext>

        {/* Loading indicator */}
        {isLoadingMore && (
          <div className="flex items-center justify-center py-3">
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--co-ink-4)]" />
          </div>
        )}

        {/* Scroll sentinel — triggers load-more via IntersectionObserver */}
        {hasMore && !isLoadingMore && tickets.length > 0 && (
          <div ref={sentinelRef} className="flex items-center justify-center py-1">
            <div className="flex gap-[3px]">
              <span className="h-1 w-1 animate-bounce rounded-full bg-[var(--co-line-strong)]" style={{ animationDelay: '0ms' }} />
              <span className="h-1 w-1 animate-bounce rounded-full bg-[var(--co-line-strong)]" style={{ animationDelay: '150ms' }} />
              <span className="h-1 w-1 animate-bounce rounded-full bg-[var(--co-line-strong)]" style={{ animationDelay: '300ms' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
});
